import api from './api';
import type { DownstreamApptSkipReason } from '../utils/legFieldGuards';

import type {
  ChangeAllPreviewRow,
  ChangeAllApplyResult,
} from './orders.service';

/**
 * The ticket fanned out by Change All. No `pronumber` (it comes from the target set)
 * and no `billto` — billto selects the THD EDI handle, so the backend reads each
 * PRO's own rather than copying the anchor's.
 */
export interface TroubleTicketChangeAllTicket {
  category: string;
  issue: string;
  assignedto: string;
  status?: string;
  driverticket?: string;
  preventable?: string;
  reason?: string;
  responsible?: string;
  carriercode?: number;
  servicefailure?: string;
}

/**
 * Embedded service-failure fields fanned out with the ticket. `responsibledriver` is
 * deliberately absent — the driver responsible on the anchor is generally not the
 * driver on the rest of the set, so it is left blank per PRO.
 */
export interface TroubleTicketChangeAllSf {
  servicefailuredate?: string | null;
  reason?: string;
  servicefailuretype?: string;
  thd_reason?: string;
  comments?: string;
}

/**
 * Trouble ticket as returned by the FastCMS backend. The optional `billto` is
 * only present on the by-recno fetch (joined from `ordersall`).
 */
/**
 * Every column on `troubleticket` is `NOT NULL` with a `DEFAULT` (string cols
 * default to '', `carriercode` to 0, `driverticket` to 'N') — so these fields
 * are non-nullable in TS too. The only nullable response fields are
 * `datetime` / `resolvedatetime`, which the backend serializes as `null` when
 * the column holds the zero-date sentinel '0000-00-00 00:00:00', and the
 * joined `billto` / `carriername` (sourced from other tables on /by-recno).
 */
export interface TroubleTicket {
  recno: number;
  pronumber: number;
  userid: string;
  datetime: string | null;
  category: string;
  status: string;
  issue: string;
  responsible: string;
  reason: string;
  assignedto: string;
  resolve: string;
  resolvedatetime: string | null;
  carriercode: number;
  driverticket: string;
  bookingnumber: string;
  preventable: string;
  servicefailure: 'Y' | 'N' | '';
  /** Reviewed stamp (TTs & Service Failures report). "Reviewed" is simply a
   *  non-blank reviewedby — there is no flag column. Set via the write-only
   *  `reviewed` toggle on update(); the backend stamps / clears both. */
  reviewedby: string;
  revieweddatetime: string | null;
  /** Only populated when fetching a single ticket via /by-recno. */
  billto?: number | null;
  /**
   * Resolved carrier name (`highway.carrierprofile.truckername`) — only
   * populated when fetching a single ticket via /by-recno. UI hint only;
   * never sent back on create/update.
   */
  carriername?: string | null;
}

/** Body of PUT /trouble-tickets/{id}: any stored column, plus the write-only
 *  Reviewed toggle (no column of its own — see TroubleTicket.reviewedby). */
export type TroubleTicketUpdatePayload = Partial<TroubleTicket> & { reviewed?: 'Y' | 'N' };

/** Category as listed by GET /trouble-tickets/categories. */
export interface TroubleTicketCategory {
  recno: number;
  category: string;
  /** 'Y' = driver-only, 'N' = non-driver, 'BOTH' = either. */
  driverticket: 'Y' | 'N' | 'BOTH';
  deleted: boolean;
}

/**
 * Payload accepted by POST /trouble-tickets. Fields stored on `troubleticket`
 * are non-nullable (the column is NOT NULL with a default — send the default,
 * not null). `billto` is only used for THD EDI event routing, not stored on
 * the ticket row, so it stays optional.
 */
export interface CreateTicketPayload {
  pronumber: number;
  category: string;
  issue: string;
  assignedto: string;
  status?: string;
  driverticket?: 'Y' | 'N';
  preventable?: 'Y' | 'N' | '';
  reason?: string;
  billto?: number | null;
  responsible?: string;
  carriercode?: number;
  servicefailure?: 'Y' | 'N' | '';
}

/** Backend wraps every response in `{ data: ... }`. */
interface Wrap<T> {
  data: T;
}

/**
 * Outcome of the server-side reschedule side effect that fires when a
 * trouble ticket is created with category="Reschedule Requested". Mirror
 * the SkipReason union in backend reschedule_trigger_service.py.
 */
export type RescheduleTriggerSkipReason =
  | 'no_delivery_leg'
  | 'no_date'
  | 'unparseable_date'
  | 'no_legs_in_group'
  | 'group_has_locked_leg';

/**
 * A leg whose arrival/departure actuals the clear is about to blank — a driver
 * who has physically MOVED, not merely one who is assigned. Surfaced as a
 * warning before anything is written; it never blocks.
 */
export interface RescheduleEnRouteLeg {
  legnum: number;
  /** Driver code, or '' when the stamps outlived the assignment. */
  driver: string;
  /** Where they are, worst case first — e.g. 'arrived at the destination'. */
  status: string;
  /** The stamp itself, as stored. */
  when: string;
}

/**
 * One of the two appointment dates the user can remove. `available` false means
 * it is not on offer at all, and `reason` says why — a skip code for the
 * delivery side, a readable sentence for the pre-pull.
 */
export interface RescheduleDateTarget {
  available: boolean;
  /** The date that would be cleared, when this target is on offer. */
  date: string | null;
  reason: string | null;
}

/** The pre-pull target also names the leg holding the date. */
export interface ReschedulePrepullTarget extends RescheduleDateTarget {
  legnum: number | null;
}

/**
 * Result of phase 1 (evaluate) — returned on the create response when the
 * category is "Reschedule Requested". Either a skip (surface a toast) or a
 * pending-confirmation (prompt the user, then call applyReschedule).
 *
 * Pending means at least ONE date is clearable. The two targets are independent
 * — an order whose delivery appointment was never set can still have a pre-pull
 * to move — so the prompt offers exactly the ones marked available.
 */
export type RescheduleEvaluateResult =
  | {
      applied: false;
      skip_reason: RescheduleTriggerSkipReason;
    }
  | {
      applied: false;
      pending_confirmation: true;
      /** The delivery date that would be cleared; null when that target is
       *  unavailable. Kept at the top level for back-compat — it has always
       *  meant the DELIVERY date. */
      servicefailuredate: string | null;
      /** The delivery leg's dispatch group. */
      delivery: RescheduleDateTarget;
      /** The pickup leg's PICKUP -> STOPOFF drop — the pre-pull. */
      prepull: ReschedulePrepullTarget;
      /** Who the clear would strip stamps from, per target. The delivery
       *  group's actuals go whatever the date answer is (for a dispatcher); the
       *  pre-pull scope's only if that date is removed. Empty for an LC, who
       *  never clears an actual. */
      en_route: {
        delivery: RescheduleEnRouteLeg[];
        prepull: RescheduleEnRouteLeg[];
      };
    };

/**
 * Result of phase 2 (apply) — returned by applyReschedule once the user has
 * answered the "remove the scheduled date?" prompt. `mode` distinguishes
 * the two clear paths; the gates can still skip here if the order changed.
 */
export type RescheduleApplyResult =
  | {
      applied: true;
      /** 'removed_date'  = delivery appt dates cleared + reschedule row created;
       *  'kept_date'     = only driver + arrival/depart actuals cleared;
       *  'prepull_only'  = the delivery side was skipped or not asked for, and
       *                    only the pre-pull moved. */
      mode: 'removed_date' | 'kept_date' | 'prepull_only';
      /** The delivery leg's dispatch group, plus the downstream legs the
       *  shared fan-out rule planned (removed_date path only), plus the
       *  pre-pull scope when that date was removed. */
      cleared_legs: number[];
      /** True when the pre-pull date was removed. */
      prepull_cleared?: boolean;
      /** The pre-pull scope that was cleared. */
      cleared_prepull_legs?: number[];
      /** The pre-pull date that was removed. */
      prepull_date?: string;
      /** Set when the pre-pull was asked for but refused by the server's
       *  re-gate (it became paid, departed, or dispatched in the meantime). */
      prepull_skipped_reason?: string;
      /** Downstream legs that KEPT their appointment dates, and why. A partial
       *  clear leaves the pro in Missing Appointment, so this has to be shown.
       *  Absent when nothing was skipped. */
      kept_appt_legs?: Array<{
        legnum: number;
        reason: DownstreamApptSkipReason;
      }>;
      /** Present on the removed_date path. */
      servicefailuredate?: string;
      /** Present only when this was a brand-new reschedule. */
      new_reschedule_count?: number;
      /** True when a matching reschedule row already existed — legs were
       *  still cleared but no new row was inserted and the count is
       *  unchanged. */
      duplicate?: boolean;
    }
  | {
      applied: false;
      skip_reason: RescheduleTriggerSkipReason;
    };

export interface CreateTicketResult {
  ticket: TroubleTicket;
  rescheduleTrigger?: RescheduleEvaluateResult;
}

interface CreateTicketResponse {
  data: TroubleTicket;
  reschedule_trigger?: RescheduleEvaluateResult;
  message?: string;
}

const troubleTicketsService = {
  /** List trouble tickets for a pro number. */
  async listByPronumber(pronumber: number, signal?: AbortSignal): Promise<TroubleTicket[]> {
    const { tickets } = await troubleTicketsService.listByPronumberWithOpenCount(
      pronumber,
      signal,
    );
    return tickets;
  },

  /**
   * The same fetch, plus the server's count of OPEN (NEW/PENDING) tickets.
   *
   * The open count is taken from the response rather than derived by filtering
   * `tickets` here, so the TTs tab's red flag uses the same definition of "open"
   * as the planner's Open TT column and the dispatch warning — one that also has
   * to survive legacy status values. Callers that only need the list keep using
   * `listByPronumber`.
   */
  async listByPronumberWithOpenCount(
    pronumber: number,
    signal?: AbortSignal,
  ): Promise<{ tickets: TroubleTicket[]; openCount: number; hasOpenRejectionToday: boolean }> {
    const response = await api.get<Wrap<TroubleTicket[]> & { openCount?: number; hasOpenRejectionToday?: boolean }>(
      `/trouble-tickets/by-pronumber/${pronumber}`,
      { signal },
    );
    return {
      tickets: response.data.data ?? [],
      openCount: response.data.openCount ?? 0,
      hasOpenRejectionToday: response.data.hasOpenRejectionToday ?? false,
    };
  },

  /** Recheck the server's current-day open rejection before prompting for a new one. */
  async hasOpenRejectionToday(pronumber: number, signal?: AbortSignal): Promise<boolean> {
    const result = await troubleTicketsService.listByPronumberWithOpenCount(pronumber, signal);
    return result.hasOpenRejectionToday;
  },

  /** Fetch a single ticket (includes joined `billto`). */
  async getByRecno(ticketId: number, signal?: AbortSignal): Promise<TroubleTicket> {
    const response = await api.get<Wrap<TroubleTicket>>(
      `/trouble-tickets/by-recno/${ticketId}`,
      { signal },
    );
    return response.data.data;
  },

  /** All active trouble-ticket categories. */
  async listCategories(signal?: AbortSignal): Promise<TroubleTicketCategory[]> {
    const response = await api.get<Wrap<TroubleTicketCategory[]>>(
      '/trouble-tickets/categories',
      { signal },
    );
    return response.data.data ?? [];
  },

  /** THD reason strings used when category starts with "THD -". */
  async listThdReasons(signal?: AbortSignal): Promise<string[]> {
    const response = await api.get<Wrap<string[]>>(
      '/trouble-tickets/thd-reasons',
      { signal },
    );
    return response.data.data ?? [];
  },

  /**
   * Reason dropdown options for a given category. Backend mirrors V1's
   * per-category reason arrays (THD, MISSED CUTOFF / LATE GATE REQUIRED,
   * REQUIRED FOR INVOICING, STORAGE, DRIVER IMPACTED). Returns `[]` for
   * categories that don't have a reason list.
   */
  async listReasonsByCategory(
    category: string,
    signal?: AbortSignal,
  ): Promise<string[]> {
    const response = await api.get<Wrap<string[]>>(
      `/trouble-tickets/reasons-by-category/${encodeURIComponent(category)}`,
      { signal },
    );
    return response.data.data ?? [];
  },

  /** Create a new ticket. The response also includes any
   *  reschedule-trigger outcome when category="Reschedule Requested" so
   *  the caller can render a toast on skip. */
  async create(payload: CreateTicketPayload): Promise<CreateTicketResult> {
    const response = await api.post<CreateTicketResponse>('/trouble-tickets', payload);
    return {
      ticket: response.data.data,
      rescheduleTrigger: response.data.reschedule_trigger,
    };
  },

  /**
   * Phase 2 of the Reschedule Requested side effect: apply the user's answer to
   * the "which dates?" prompt. Called after create when the evaluate result was
   * `pending_confirmation`.
   *
   * The two answers are independent and both default false, so neither can be
   * sent by accident. The server re-gates each one against freshly-read state —
   * a target it refuses comes back reported, not as an error.
   */
  async applyReschedule(
    pronumber: number,
    answer: { removeScheduledDate?: boolean; removePrepullDate?: boolean },
    /** recno of the Reschedule Requested ticket created in phase 1 —
     *  stamped on the new reschedule row as troubleticketrecno. */
    troubleTicketRecno: number = 0,
  ): Promise<RescheduleApplyResult> {
    const response = await api.post<Wrap<RescheduleApplyResult>>(
      '/trouble-tickets/reschedule',
      {
        pronumber,
        remove_scheduled_date: !!answer.removeScheduledDate,
        remove_prepull_date: !!answer.removePrepullDate,
        troubleticket_recno: troubleTicketRecno,
      },
    );
    return response.data.data;
  },

  /**
   * Change All — per-PRO eligibility for creating this ticket across the target set.
   * CREATE only; there is no bulk edit or bulk delete of tickets/service failures.
   */
  async changeAllPreview(
    body: {
      pros: (number | string)[];
      ticket: TroubleTicketChangeAllTicket;
      /** The two date answers, already given — they make the per-pro warnings
       *  concrete (which pros have no pre-pull, who is about to lose actuals). */
      removeScheduledDate?: boolean;
      removePrepullDate?: boolean;
    },
    signal?: AbortSignal,
  ): Promise<{ rows: ChangeAllPreviewRow[] }> {
    const response = await api.post('/orders/change-all/trouble-ticket/preview', body, {
      signal,
    });
    return response.data;
  },

  /**
   * Change All — create the ticket on every PRO in the chunk. Each PRO commits in its
   * own transaction with its own side effects (email, THD EDI from that PRO's billto);
   * `submitNonce` makes a re-fire a no-op rather than a double-create.
   */
  async changeAllApply(
    body: {
      pros: (number | string)[];
      ticket: TroubleTicketChangeAllTicket;
      serviceFailure?: TroubleTicketChangeAllSf | null;
      submitNonce?: string;
      /** The two "which dates?" answers, given once and applied to every pro.
       *  Only read for a Reschedule Requested ticket. */
      removeScheduledDate?: boolean;
      removePrepullDate?: boolean;
    },
  ): Promise<{ results: ChangeAllApplyResult[] }> {
    const response = await api.post('/orders/change-all/trouble-ticket/apply', body);
    return response.data;
  },

  /** Partial-update an existing ticket. `reviewed` is a write-only toggle: 'Y'
   *  stamps reviewedby / revieweddatetime server-side, 'N' clears them. */
  async update(
    ticketId: number,
    payload: TroubleTicketUpdatePayload,
  ): Promise<TroubleTicket> {
    const response = await api.put<Wrap<TroubleTicket>>(
      `/trouble-tickets/${ticketId}`,
      payload,
    );
    return response.data.data;
  },
};

export default troubleTicketsService;
